import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Card,
  CardBody,
  Form
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import CustomMonthPicker from "common/CustomMonthPicker";
import { useFormik } from "formik";
import * as Yup from "yup";
import TableContainer from "components/Common/TableContainer";
import { changeExtraPaymentPayrollStatus, fetchEmployeeDropdownAutoList, fetchExtraPaymentPayroll, saveExtraPaymentPayroll } from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import CustomSelect from "components/Common/custom-select";
import Swal from "sweetalert2";
import { convertToInputDate } from "common/dateFormat/formatDate";
import dayjs from "dayjs";

const validationSchema = Yup.object({
  employee: Yup.object().required("Employee is required"),
  pendingMonth: Yup.date().required("Pending Month is required").typeError("Pending Month must be a valid date"),
  payMonth: Yup.date().required("Pay Month is required").typeError("Pay Month must be a valid date").test("payMonth-after-pendingMonth", "Pay Month must be on or after Pending Month", function (value) {
    const { pendingMonth } = this.parent;
    return !pendingMonth || !value || value > pendingMonth;
  }),
  noOfDays: Yup.number().typeError("No Of Days must be a number").required("No Of Days is required").positive("No Of Days must be greater than 0").integer("No Of Days must be an integer"),
});

const ExtraPayment = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    dropdownAutoList,
    extraPayments,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList,
    extraPayments: state.PayrollSalaryExtraPaymentReducer?.extraPayments,

    loading: state.PayrollSalaryExtraPaymentReducer?.loading,
    error: state.PayrollSalaryExtraPaymentReducer?.error,
    success: state.PayrollSalaryExtraPaymentReducer?.success
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchEmployeeDropdownAutoList(unitId));
    dispatch(fetchExtraPaymentPayroll(unitId))
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchExtraPaymentPayroll(authDetails?.unit_id));
      formik.resetForm();
    }
  }, [authDetails, success, dispatch]);

  const formik = useFormik({
    initialValues: {
      employee: "",
      pendingMonth: "",
      payMonth: "",
      noOfDays: "",
    },
    validationSchema,
    onSubmit: (values) => {
      console.log("Submitted values:", values);
      const formData = new FormData();
      formData.append("emp_id", values?.employee?.value);
      formData.append("pending_month", values?.pendingMonth);
      formData.append("pay_month", values?.payMonth);
      formData.append("no_of_days", values?.noOfDays);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveExtraPaymentPayroll(formData));
    },
  });

  const columns = [
    {
      Header: "S No",
      Cell: ({ row }) => extraPayments.length - row.index,
    },
    {
      Header: "Code",
      accessor: "emp_no",
    },
    {
      Header: "Name",
      accessor: "emp_name",
    },
    {
      Header: "Pending Month",
      accessor: "pending_month",
    },
    {
      Header: "Pay Month",
      accessor: "pay_month",
    },
    {
      Header: "No of Days",
      accessor: "no_of_days",
    },
    {
      Header: "Amount",
      accessor: "amount",
    },
    {
      Header: "Action",
      Cell: (cellProps) => (
        <div className="text-danger">
          <i
            className="mdi mdi-delete font-size-18"
            style={{ cursor: "pointer" }}
            onClick={() => handleDelete(cellProps.row.original)}
          />
        </div>
      ),
    },
  ];

  const handleDelete = (row) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        // formData.append("pay_month", convertToInputDate(row.pay_month));
        formData.append("pay_month", row?.api_paymonth);
        formData.append("emp_id", row?.emp_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeExtraPaymentPayrollStatus(formData));
      }
    });
  };

  const maxMonth = dayjs().add(1, "month");

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Payroll / Salary" breadcrumbItem="Extra Payment" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={3} sm={12} className="mb-3">
                        <FormGroup>
                          <Label for="employee">Employee<span className="text-danger">*</span></Label>
                          <CustomSelect
                            id="employee"
                            name="employee"
                            options={dropdownAutoList.map(cat => ({ value: cat.emp_id.toString(), label: cat.emp_no }))}
                            value={formik.values.employee || null}  // Store full object
                            onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                            onBlur={formik.handleBlur}
                            className={formik.touched.employee && formik.errors.employee ? 'is-invalid' : ''}
                          />
                          {formik.touched.employee && formik.errors.employee && (
                            <div className="text-danger">{formik.errors.employee}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for="pendingMonth">
                            Pending Month <span className="text-danger">*</span>
                          </Label>
                          <CustomMonthPicker
                            name="pendingMonth"
                            value={formik.values.pendingMonth}
                            onChange={(date) => formik.setFieldValue("pendingMonth", date)}
                            onBlur={formik.handleBlur}
                            disableFuture={true}
                            className={`form-control ${formik.touched.pendingMonth && formik.errors.pendingMonth ? "is-invalid" : ""}`}
                          />
                          {formik.touched.pendingMonth && formik.errors.pendingMonth && (
                            <div className="text-danger">{formik.errors.pendingMonth}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for="payMonth">
                            Pay Month <span className="text-danger">*</span>
                          </Label>
                          <CustomMonthPicker
                            name="payMonth"
                            value={formik.values.payMonth}
                            onChange={(date) => formik.setFieldValue("payMonth", date)}
                            onBlur={formik.handleBlur}
                            maxDate={maxMonth}
                            className={`form-control ${formik.touched.payMonth && formik.errors.payMonth ? "is-invalid" : ""}`}
                          />
                          {formik.touched.payMonth && formik.errors.payMonth && (
                            <div className="text-danger">{formik.errors.payMonth}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={3}>
                        <FormGroup>
                          <Label for="noOfDays">
                            No Of Days <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="number"
                            id="noOfDays"
                            name="noOfDays"
                            value={formik.values.noOfDays}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={formik.touched.noOfDays && formik.errors.noOfDays ? "is-invalid" : ""}
                          />
                          {formik.touched.noOfDays && formik.errors.noOfDays && (
                            <div className="text-danger">{formik.errors.noOfDays}</div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={12} sm={12} className="d-flex align-items-center justify-content-end">
                        <Button type="submit" className="btn button-save me-2">
                          <i className="fas fa-save me-1" /> Save
                        </Button>
                        <Button
                          type="button"
                          className="btn button-cancel"
                          onClick={() => {
                            Swal.fire({
                              title: "Are you sure?",
                              text: "All entered data will be cleared.",
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonText: "Yes, Reset",
                              cancelButtonText: "No",
                            }).then((result) => {
                              if (result.isConfirmed) {
                                formik.handleReset();
                              }
                            });
                          }}
                        >
                          <i className="fas fa-sync me-1" /> Cancel
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
              </Card>
              {/* <Card>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label for="employeeName">Employee Name</Label>
                        <Input type="text" id="employeeName" />
                      </FormGroup>
                    </Col>
                    <Col md={8} className="d-flex align-items-center justify-content-end">
                      <Button type="submit" className="btn button-search me-2">
                        <i className="fas fa-search me-1" /> Search
                      </Button>

                    </Col>
                  </Row>
                </CardBody>
              </Card> */}
              <Card>
                <CardBody>
                  <TableContainer columns={columns} data={extraPayments && extraPayments.length > 0 ? extraPayments : []} isGlobalFilter={true} customPageSize={10} />
                  {/* Employee Data Table */}
                  {/* <Table bordered striped>
                    <thead className="bg-secondary text-white">
                      <tr>
                        <th>S No</th>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Pending Month</th>
                        <th>Pay Month</th>
                        <th>No of Days</th>
                        <th>Amount</th>
                        <th>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp, index) => (
                        <tr key={emp.id}>
                          <td>{index + 1}</td>
                          <td>{emp.code}</td>
                          <td>{emp.name}</td>
                          <td>{emp.pendingMonth}</td>
                          <td>{emp.payMonth}</td>
                          <td>{emp.days.toFixed(2)}</td>
                          <td>{emp.amount.toFixed(2)}</td>
                          <td className="text-center">
                            <Button size="sm" className="btn button-delete"><i className="fas fa-trash" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table> */}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ExtraPayment;
